import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import MediaPlayerStyle from '../assets/css/MediaPlayer.module.css';
import { ReactComponent as LoaderIcon } from '../assets/images/loader.svg';

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

const HlsPlayer = ({ src, poster }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [buffering, setBuffering] = useState(false);
  const initialLoadingRef = useRef(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    setInitialLoading(true);
    setBuffering(false);
    initialLoadingRef.current = true;

    const onEnded = () => { video.currentTime = 0; };
    const onLoadedData = () => {
      setInitialLoading(false);
      initialLoadingRef.current = false;
    };
    const onWaiting = () => {
      if (!initialLoadingRef.current) setBuffering(true);
    };
    const onPlaying = () => {
      setInitialLoading(false);
      initialLoadingRef.current = false;
      setBuffering(false);
    };

    video.addEventListener('ended', onEnded);
    video.addEventListener('loadeddata', onLoadedData);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('playing', onPlaying);

    if (Hls.isSupported()) {
      const hls = new Hls({
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
      });
      hlsRef.current = hls;
      hls.loadSource(src);
      hls.attachMedia(video);

      const originalPlay = video.play.bind(video);
      video.play = () => originalPlay().catch(err => {
        if (err.name !== 'AbortError') throw err;
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          hls.startLoad();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
        } else {
          hls.destroy();
        }
      });

      return () => {
        video.removeEventListener('ended', onEnded);
        video.removeEventListener('loadeddata', onLoadedData);
        video.removeEventListener('waiting', onWaiting);
        video.removeEventListener('playing', onPlaying);
        hls.destroy();
        hlsRef.current = null;
      };
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      return () => {
        video.removeEventListener('ended', onEnded);
        video.removeEventListener('loadeddata', onLoadedData);
        video.removeEventListener('waiting', onWaiting);
        video.removeEventListener('playing', onPlaying);
      };
    }
  }, [src]);

  return (
    <div className={MediaPlayerStyle.element}>
      <div className={MediaPlayerStyle.picture}>
        {initialLoading && (
          <div className={MediaPlayerStyle.loadingContainer}>
            <LoaderIcon className={'loader'} height={40} width={40} />
          </div>
        )}
        {buffering && isSafari && (
          <div className={MediaPlayerStyle.loadingContainerOverlay}>
            <LoaderIcon className={'loader'} height={40} width={40} />
          </div>
        )}
        <video
          ref={videoRef}
          controls
          className={MediaPlayerStyle.pictureImg}
          poster={poster ?? undefined}
          style={{ display: initialLoading ? 'none' : 'block' }}
        />
      </div>
    </div>
  );
};

export default HlsPlayer;
